import React, { Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  ContentFilterFacebookView,
  ContentFilterFacebookViewProps,
  FacebookSearchTypesEnum
} from './ContentFilterFacebook.view';
import { UseFormReturn, useWatch } from 'react-hook-form';
import {
  FacebookSearchContentType,
  FacebookSearchSortType,
  FacebookSearchType,
  GraphQLTypes,
  ImportSourceArgsFacebookInput,
  ImportSourceArgsGroupInput,
  SocialOAuthProvider,
  SocialProfileFields_FacebookSocialProfile_Fragment,
  ThirdPartyProfile
} from '../../../../models/graphql';
import { fetchFacebookParameters } from './ContentFilterFacebook.apollo';
import { FindContentContext, SearchStepEnum } from '../FindContentProvider';
import { getApolloErrorMessage, remapApolloErrors } from '../../../../helpers/remapApolloErrors';
import { ApolloError, useApolloClient } from '@apollo/client';
import { useDidMount } from '../../../../hooks/useDidMount';
import { PostSourceType } from '../../../../constants/find-content';
import { TreeMenuContext } from '../TreeMenu/TreeMenuProvider';
import { FormElementValueType } from '../../../../models/basic-objects';
import { getAllApolloCacheEntitiesOfType } from '../../../../helpers/apolloCacheHelpers';

export type ContentFilterFacebookControllerProps = {
  formInstance: UseFormReturn;
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  thirdPartyProfiles: ThirdPartyProfile[];
  searchParameters: fetchFacebookParameters | undefined;
  setSearchParameters: Dispatch<SetStateAction<fetchFacebookParameters | undefined>>;
  fetchFacebookPosts: (parameters: fetchFacebookParameters) => Promise<void>;
  fetchThirdPartyProfiles: (profileId: string, profileName: string) => Promise<void>;
  purgeFacebookCache: () => void;
  autoCompleteLoading: boolean;
  firstConnectedPageProfileId?: string;
  profileSearchHistory: ThirdPartyProfile[];
};

export const ContentFilterFacebookController = (props: ContentFilterFacebookControllerProps): JSX.Element => {
  const {
    searchParameters,
    setSearchParameters,
    formInstance,
    setShowFilters,
    fetchFacebookPosts,
    purgeFacebookCache,
    fetchThirdPartyProfiles,
    thirdPartyProfiles,
    firstConnectedPageProfileId
  } = props;

  const [showSaveSourceModal, setShowSaveSourceModal] = useState<boolean>(false);
  const { modalType, selectedMenuItem, setSelectedMenuItem } = useContext(TreeMenuContext);
  const [apolloErrorKey, setApolloErrorKey] = useState<string | undefined>(undefined);
  const { posts, setError, setSourceType, setSelectedPosts, profiles, setSearchStep, setHasSearched, onSearchResult } =
    useContext(FindContentContext);
  const typeValue = useWatch({ name: 'type', control: formInstance.control });
  const [selectedAutoCompleteValue, setSelectedAutoCompleteValue] = useState<FormElementValueType>('');
  const { cache } = useApolloClient();

  const facebookProfiles = useMemo(() => {
    return profiles.filter(
      p => p.__typename === 'FacebookSocialProfile'
    ) as SocialProfileFields_FacebookSocialProfile_Fragment[];
  }, [profiles]);

  const getFacebookProfile = useCallback(
    (profileId: string) => {
      return facebookProfiles.find(p => p.id === profileId)?.facebookId || '';
    },
    [facebookProfiles]
  );

  const getFacebookPosts = useCallback(
    (parameters: fetchFacebookParameters) => {
      purgeFacebookCache();

      // Used for the filtering of the results
      setSearchParameters(parameters);

      fetchFacebookPosts(parameters)
        .then(() => {
          onSearchResult(parameters, facebookProfiles, !!parameters.isExternal, thirdPartyProfiles);
          setShowFilters(true);
          setError(undefined);
        })
        .catch(error => {
          const apolloErrorType = getApolloErrorMessage(error);
          const remappedError = remapApolloErrors(error as ApolloError);

          setApolloErrorKey(apolloErrorType);
          setError(remappedError);
        });
    },
    [
      fetchFacebookPosts,
      purgeFacebookCache,
      setError,
      setSearchParameters,
      setShowFilters,
      onSearchResult,
      facebookProfiles,
      thirdPartyProfiles
    ]
  );

  const clearFilterBarForm = useCallback(() => {
    const elementNames = ['contentType', 'sortType', 'term'];
    elementNames.forEach(e => formInstance.resetField(e));

    setSearchStep(SearchStepEnum.ONE);
  }, [formInstance, setSearchStep]);

  const clearFacebookForm = useCallback(
    (excluded: string) => {
      const elementNames = [
        ...Object.values(FacebookSearchType),
        ...Object.values(FacebookSearchTypesEnum),
        ...['contentType', 'sortType', 'term', 'pages']
      ];

      elementNames
        .filter(n => n !== excluded)
        .forEach(e => {
          formInstance.resetField(e);
        });
    },
    [formInstance]
  );

  const submitFormHandler = useCallback(
    (search: FacebookSearchType | FacebookSearchTypesEnum, value: string) => {
      clearFilterBarForm();

      const externalId = getFacebookProfile(value);
      const baseParameters = {
        contentType: formInstance.getValues('contentType') ?? FacebookSearchContentType.ALL,
        sortType: formInstance.getValues('sortType') ?? FacebookSearchSortType.LATEST
      };

      switch (search) {
        case FacebookSearchTypesEnum.FACEBOOK_PAGES:
          // Only process if we have a value
          if (value) {
            fetchThirdPartyProfiles(firstConnectedPageProfileId || '', value).catch(error => {
              setError(remapApolloErrors(error as ApolloError));
            });
          }
          break;
        case FacebookSearchType.PAGE:
          if (firstConnectedPageProfileId) {
            getFacebookPosts({
              ...baseParameters,
              searchType: FacebookSearchType.PAGE,
              externalId: value,
              socialProfileId: firstConnectedPageProfileId,
              isExternal: true
            });
          } else {
            // We should never have an undefined defaultProfileId, keeps TS happy
            setError('You do not have any connected facebook profiles.');
          }
          break;
        case FacebookSearchTypesEnum.MY_POSTS:
          getFacebookPosts({
            ...baseParameters,
            searchType: FacebookSearchType.PAGE,
            externalId,
            socialProfileId: value
          });
          break;
        case FacebookSearchType.GROUP:
          getFacebookPosts({
            ...baseParameters,
            searchType: FacebookSearchType.GROUP,
            externalId,
            socialProfileId: value
          });
          break;
        default:
          // Don't show filters if we fall through
          setShowFilters(false);
          break;
      }
    },
    [
      clearFilterBarForm,
      getFacebookProfile,
      formInstance,
      firstConnectedPageProfileId,
      getFacebookPosts,
      setShowFilters,
      fetchThirdPartyProfiles,
      setError
    ]
  );

  const filterSearchHandler = useCallback(
    (resetFilterBar?: boolean, ignoreStepIncrement?: boolean) => {
      if (resetFilterBar) {
        clearFilterBarForm();
      }

      if (searchParameters) {
        purgeFacebookCache();
        setError(undefined);

        if (!ignoreStepIncrement) {
          setSearchStep(SearchStepEnum.TWO);
        }

        const params: fetchFacebookParameters = {
          ...searchParameters,
          ...{
            sortType: formInstance.getValues('sortType'),
            contentType: formInstance.getValues('contentType'),
            term: formInstance.getValues('term') ?? undefined
          }
        };

        getFacebookPosts(params);
      }
    },
    [clearFilterBarForm, formInstance, getFacebookPosts, purgeFacebookCache, searchParameters, setError, setSearchStep]
  );

  const getFacebookInputArgs = useCallback((): ImportSourceArgsGroupInput => {
    const { type, contentType, sortType, term } = formInstance.getValues();

    const socialProfileId =
      type === FacebookSearchTypesEnum.MY_POSTS ? searchParameters?.socialProfileId ?? facebookProfiles[0].id : null;

    const facebookProfile = facebookProfiles.find(p => p.id === socialProfileId);
    const externalProfiles = getAllApolloCacheEntitiesOfType(cache, GraphQLTypes.ThirdPartyProfile);

    const thirdPartyProfileDisplayName =
      type === FacebookSearchTypesEnum.MY_POSTS
        ? facebookProfile?.displayName
        : externalProfiles.find(p => p.externalId === searchParameters?.externalId)?.displayName;

    const searchType = type === FacebookSearchTypesEnum.MY_POSTS ? FacebookSearchType.PAGE : type;
    const externalId = searchParameters?.externalId ?? facebookProfile?.facebookId;

    const requiredArgs: ImportSourceArgsFacebookInput = {
      socialProfileId,
      externalId,
      contentType,
      sortType,
      searchType,
      thirdPartyProfileDisplayName,
      term: term ?? '' // Always check and return empty string if null/undefined
    };

    const args = {
      ...requiredArgs
    };

    return { facebook: args };
  }, [cache, facebookProfiles, formInstance, searchParameters]);

  const resetCallBackHandler = useCallback(() => {
    clearFacebookForm('type');
    setSelectedAutoCompleteValue(undefined);
    setSelectedPosts([]);
    purgeFacebookCache();
    setSearchStep(SearchStepEnum.ONE);
    setHasSearched(false);
    setShowFilters(false);
  }, [clearFacebookForm, purgeFacebookCache, setHasSearched, setSearchStep, setSelectedPosts, setShowFilters]);

  // Prefill with SavedSource
  useEffect(() => {
    if (
      !modalType &&
      selectedMenuItem &&
      selectedMenuItem.__typename === 'ImportSource' &&
      selectedMenuItem.args.__typename === 'ImportSourceArgsFacebook'
    ) {
      purgeFacebookCache();

      const { args } = selectedMenuItem;
      setError(undefined);

      const type =
        facebookProfiles.find(p => p.id === String(args.socialProfileId))?.facebookId === args.externalId
          ? FacebookSearchTypesEnum.MY_POSTS
          : args.facebookSearchType;
      const socialProfileId = String(args.socialProfileId ?? facebookProfiles.find(p => p.connected)?.id);

      if (type === FacebookSearchTypesEnum.MY_POSTS) {
        formInstance.setFocus(FacebookSearchTypesEnum.MY_POSTS);
      }

      formInstance.reset({
        ...args,
        platform: SocialOAuthProvider.FACEBOOK,
        type
      });

      const parameters: fetchFacebookParameters = {
        socialProfileId: socialProfileId,
        externalId: String(args.externalId),
        searchType: args.facebookSearchType,
        sortType: args.facebookSortType,
        contentType: args.facebookContentType,
        term: String(args.term)
      };

      fetchFacebookPosts(parameters)
        .then(() => {
          setShowFilters(true);
          onSearchResult(parameters, facebookProfiles, false);
          setSelectedMenuItem(undefined);
        })
        .catch(error => {
          setError(remapApolloErrors(error as ApolloError));
        })
        .finally(() => {
          formInstance.setValue(FacebookSearchTypesEnum.MY_POSTS, String(args.socialProfileId));
          formInstance.setValue('contentType', args.facebookContentType);
          formInstance.setValue('sortType', args.facebookSortType);

          setSearchParameters(parameters);
        });
    }
  }, [
    facebookProfiles,
    fetchFacebookPosts,
    formInstance,
    modalType,
    purgeFacebookCache,
    selectedMenuItem,
    setError,
    setSearchParameters,
    setSelectedMenuItem,
    setShowFilters,
    onSearchResult
  ]);

  // Clear all OTHER form values when type is changed
  useEffect(() => {
    setSearchParameters(undefined);
    clearFacebookForm(typeValue);
    setSelectedAutoCompleteValue(undefined);
    setSearchStep(SearchStepEnum.ONE);
    purgeFacebookCache();
    setError(undefined);
    setShowFilters(false);
  }, [
    clearFacebookForm,
    formInstance,
    purgeFacebookCache,
    setError,
    setSearchParameters,
    setSearchStep,
    setShowFilters,
    typeValue
  ]);

  useDidMount(() => {
    setError(undefined);
    setHasSearched(false);
    setSourceType(PostSourceType.FacebookSocialProfile);

    if (facebookProfiles.length < 1) {
      formInstance.setValue('type', -1);
    } else {
      formInstance.setValue('type', FacebookSearchType.PAGE);
    }

    return () => {
      setSourceType(undefined);
      clearFacebookForm('');
      setSelectedPosts([]);
      purgeFacebookCache();
    };
  });

  const viewProps: ContentFilterFacebookViewProps = {
    ...props,
    submitFormHandler,
    filterSearchHandler,
    selectedAutoCompleteValue,
    setSelectedAutoCompleteValue,
    hasPosts: posts.length > 0,
    showSaveSourceModal,
    setShowSaveSourceModal,
    getFacebookInputArgs,
    thirdPartyProfiles,
    facebookProfiles,
    apolloErrorKey,
    resetCallBackHandler
  };

  return <ContentFilterFacebookView {...viewProps} />;
};
